import { Badge } from '@fsd/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface ProductImageGalleryProps {
  images: string[];
  name: string;
  inStock: boolean;
  condition?: string;
  locale: string;
}

/**
 * Component for displaying product image gallery with thumbnails
 */
export function ProductImageGallery({
  images,
  name,
  inStock,
  condition,
  locale,
}: ProductImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handlePrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
  };

  return (
    <div className="relative">
      {/* Main Image */}
      <div className="relative aspect-square overflow-hidden rounded-2xl bg-slate-100 dark:bg-slate-800">
        {!inStock && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-black/60 backdrop-blur-sm">
            <Badge variant="danger" size="lg" className="bg-red-500 text-white shadow-lg">
              {locale === 'pt-BR'
                ? 'Fora de estoque'
                : locale === 'en-US'
                  ? 'Out of stock'
                  : 'Sin stock'}
            </Badge>
          </div>
        )}
        {condition && (
          <Badge
            variant="secondary"
            size="sm"
            className="absolute top-4 right-4 z-10 shadow-lg font-bold"
          >
            {condition}
          </Badge>
        )}

        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition-all hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Next image"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        <img
          src={images[selectedIndex]}
          alt={`${name} - Imagem ${selectedIndex + 1}`}
          className="h-full w-full object-cover transition-opacity duration-300"
          loading="eager"
        />

        {/* Image Counter */}
        {images.length > 1 && (
          <Badge
            variant="outline"
            size="sm"
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white border-none"
          >
            {selectedIndex + 1} / {images.length}
          </Badge>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              key={image}
              type="button"
              onClick={() => handleThumbnailClick(index)}
              className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary ${
                selectedIndex === index
                  ? 'border-primary ring-2 ring-primary'
                  : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
              }`}
              aria-label={`View image ${index + 1}`}
            >
              <img
                src={image}
                alt={`${name} - Thumbnail ${index + 1}`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
              {selectedIndex === index && <div className="absolute inset-0 bg-primary/20" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
